/**
 * EmployeeDetail.jsx – Vollständige digitale Personalakte
 * Route: /admin/personal/mitarbeiter/:employeeId
 * Zeigt: Stammdaten | Personalbogen | Verträge | Audit-Log
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import SignaturePad from '../../components/ui/SignaturePad';
import {
  ArrowLeft, ArrowRightLeft, User, FileText, Shield, Clock, CheckCircle2,
  Edit2, Save, X, Loader2, FileDown, Key, Trash2,
  RefreshCw, AlertTriangle, Package, Mail, Scale, Link as LinkIcon, Copy,
  PenLine, ChevronDown,
} from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { PersonalboqenEditModal } from '../../components/PersonalboqenEditModal';
import { PersonalboqenWizard } from '../../components/PersonalboqenWizard';
import { ArbeitszeugnisModal } from '../../components/ArbeitszeugnisModal';
import { AbmahnungModal } from '../../components/AbmahnungModal';
import { KuendigungsAssistent } from '../../components/KuendigungsAssistent';
import { PersonaldatenSection } from '../../components/PersonaldatenSection';
import { UpgradeHint } from '../../components/UpgradeHint';
import { useModules } from '../../contexts/ModulesContext';
import { useAuth } from '../../contexts/AuthContext';
import { BeschaeftigungsWechselModal } from '../../components/BeschaeftigungsWechselModal';
import { PersonalakteOverview } from '../../components/PersonalakteOverview';
import { EmployeeDokumenteTab } from '../../components/EmployeeDokumenteTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABELS = { admin: 'Admin', stationsleitung: 'Stationsleitung', employee: 'Mitarbeiter', superadmin: 'Superadmin' };
const ROLE_COLORS = { admin: 'bg-blue-100 text-blue-700', stationsleitung: 'bg-violet-100 text-violet-700', employee: 'bg-gray-100 text-gray-700' };

const AKTE_TABS = [
  { id: 'uebersicht',     label: 'Übersicht',               icon: CheckCircle2 },
  { id: 'personaldaten',  label: 'Personaldaten',           icon: User },
  { id: 'beschaeftigung', label: 'Beschäftigung',           icon: ArrowRightLeft },
  { id: 'dokumente',      label: 'Dokumente & Unterschriften', icon: FileText },
  { id: 'unterweisungen', label: 'Unterweisungen',          icon: Shield },
  { id: 'ausstattung',    label: 'Ausstattung',             icon: Package },
  { id: 'verlauf',        label: 'Verlauf',                 icon: Clock },
];

export default function EmployeeDetail() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { isModuleEnabled } = useModules();
  const { user } = useAuth();

  // Einfacher Wrapper: Sektion mit Upgrade-Hinweis umschließen wenn gesperrt
  const withUpgrade = (moduleKey, label, content) => {
    if (isModuleEnabled(moduleKey)) return content;
    return (
      <UpgradeHint moduleName={label} requiredPackage="business" currentPackage="starter">
        {content}
      </UpgradeHint>
    );
  };
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [bogen, setBogen] = useState(null);
  const [signing, setSigning] = useState(null);
  const [rvSigning, setRvSigning] = useState(null);
  const [akteStatus, setAkteStatus] = useState(null);
  const [sendingRvEmail, setSendingRvEmail] = useState(false);
  const [creatingRvSigning, setCreatingRvSigning] = useState(false);
  // Personalbogen-RV: Arbeitgeber-Gegenzeichnung (System B)
  const [showRvSignPb, setShowRvSignPb] = useState(false);
  const [rvPbSigEmpty, setRvPbSigEmpty] = useState(true);
  const [rvPbSaving, setRvPbSaving] = useState(false);
  const rvPbSigRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetPwLoading, setResetPwLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPersonalbogenCard, setShowPersonalbogenCard] = useState(false);
  const [showArbeitsvertragCard, setShowArbeitsvertragCard] = useState(false);
  const [showStammdatenCard, setShowStammdatenCard] = useState(false);
  const [showRvCard, setShowRvCard] = useState(false);
  const [editingHW, setEditingHW] = useState(false);   // FIX: wurde beim Refactor versehentlich entfernt
  const [hwForm, setHwForm] = useState({});
  const [savingHW, setSavingHW] = useState(false);
  // Accordion: Hardware + HR-Bereiche (eingeklappt wenn leer)
  const [showHardware, setShowHardware] = useState(false);
  const [showZeugnisSection, setShowZeugnisSection] = useState(false);
  const [showAbmahnungSection, setShowAbmahnungSection] = useState(false);
  const [showKuendigungSection, setShowKuendigungSection] = useState(false);
  // Hardware Signature Modal
  const [hwSigModal, setHwSigModal] = useState(null); // { type: 'ausgabe'|'rueckgabe', datum }
  const hwSigRef = React.useRef(null);
  const [hwSigEmpty, setHwSigEmpty] = useState(true);
  const [savingHwSig, setSavingHwSig] = useState(false);
  // Retroactive onboarding state
  const [creatingBogen, setCreatingBogen] = useState(false);
  const [retroBogenLink, setRetroBogenLink] = useState(null); // { url, pin }
  const [editRetroModal, setEditRetroModal] = useState(null); // bogen obj for admin-fill modal
  const [showBogenWizard, setShowBogenWizard] = useState(false);
  const [locations, setLocations] = useState([]);
  const [showZeugnis, setShowZeugnis] = useState(false);
  const [editingZeugnis, setEditingZeugnis] = useState(null); // null = neu, object = bearbeiten
  const [zeugnisse, setZeugnisse] = useState([]);
  const [downloadingZeugnis, setDownloadingZeugnis] = useState(null);
  const [contractLinkInfo, setContractLinkInfo] = useState(null); // { url, pin_code }
  const [generatingContractLink, setGeneratingContractLink] = useState(false);
  // AG Unterschrift-Dialog
  const [showSignAG, setShowSignAG] = useState(false);
  const [agSigEmpty, setAgSigEmpty] = useState(true);
  const [agSaving, setAgSaving] = useState(false);
  const agSigRef = useRef(null);
  const [showAbmahnung, setShowAbmahnung] = useState(false);
  const [abmahnungen, setAbmahnungen] = useState([]);
  const [downloadingAbmahnung, setDownloadingAbmahnung] = useState(null);
  const [showKuendigungsAssistent, setShowKuendigungsAssistent] = useState(false);
  const [showBeschaeftigungsWechsel, setShowBeschaeftigungsWechsel] = useState(false);
  const [kuendigungen, setKuendigungen] = useState([]);

  const f = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  useEffect(() => { fetchAll(); }, [employeeId]);

  const fetchAll = async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const [empR, boqR, locR] = await Promise.allSettled([
        axios.get(`${API}/admin/employees/${employeeId}`, { headers }),
        axios.get(`${API}/personalbogen`, { headers }),
        axios.get(`${API}/admin/locations`, { headers }),
      ]);
      if (locR.status === 'fulfilled') setLocations(locR.value.data || []);

      // Zeugnisse laden
      try {
        const zR = await axios.get(`${API}/arbeitszeugnis/by-employee/${employeeId}`, { headers });
        setZeugnisse(zR.data || []);
      } catch { /* keine Zeugnisse */ }

      // Abmahnungen laden
      try {
        const abR = await axios.get(`${API}/abmahnung/by-employee/${employeeId}`, { headers });
        setAbmahnungen(abR.data || []);
      } catch { /* keine Abmahnungen */ }

      // Kündigungen laden
      try {
        const kR = await axios.get(`${API}/kuendigung/by-employee/${employeeId}`, { headers });
        setKuendigungen(kR.data || []);
      } catch { /* keine Kündigungen */ }
      if (empR.status === 'fulfilled') {
        setEmployee(empR.value.data);
        setForm(empR.value.data);
        setHwForm({
          chip_number:         empR.value.data.chip_number || '',
          key_number:          empR.value.data.key_number || '',
          received_hardware_at: empR.value.data.received_hardware_at || '',
          returned_hardware_at: empR.value.data.returned_hardware_at || '',
        });
      }
      if (boqR.status === 'fulfilled') {
        const allBoqens = boqR.value.data || [];
        const emp = empR.status === 'fulfilled' ? empR.value.data : null;
        // Find bogen linked to this employee: by employee_id field, or by email match
        const linked = allBoqens.find(b =>
          b.employee_id === employeeId ||
          b.user_id === employeeId ||
          (b.personal?.email && emp?.email && b.personal.email.toLowerCase() === emp.email.toLowerCase())
        );
        if (linked) {
          setBogen(linked);
          // Signing laden: 1) via contract_signing_id im Bogen, 2) direkt per employee_id (Direktvertrag)
          let signingLoaded = false;
          if (linked.contract_signing_id) {
            try {
              const sigR = await axios.get(`${API}/contracts/signing/${linked.contract_signing_id}`, { headers });
              if (sigR.data?.id) { setSigning(sigR.data); signingLoaded = true; }
            } catch { /* no signing */ }
          }
          // Fallback: Direktvertrag des Mitarbeiters suchen
          if (!signingLoaded) {
            try {
              const sigR2 = await axios.get(`${API}/contracts/signing/by-employee/${employeeId}`, { headers });
              if (sigR2.data?.id) setSigning(sigR2.data);
            } catch { /* no signing via employee_id */ }
          }
        } else {
          // Kein Personalbogen, aber vielleicht Direktvertrag
          try {
            const sigR3 = await axios.get(`${API}/contracts/signing/by-employee/${employeeId}`, { headers });
            if (sigR3.data?.id) setSigning(sigR3.data);
          } catch { /* no signing */ }
        }
      }
      // RV-Befreiungsantrag laden
      try {
        const rvR = await axios.get(`${API}/contracts/rv-signing/by-employee/${employeeId}`, { headers });
        if (rvR.data?.id) setRvSigning(rvR.data);
        else setRvSigning(null);
      } catch { setRvSigning(null); }

      // Konsolidierter Akten-/RV-Status (Phase 1: fasst Personalbogen UND Vertrags-Signing zusammen)
      try {
        const asR = await axios.get(`${API}/personalbogen/employee/${employeeId}/akte-status`, { headers });
        setAkteStatus(asR.data || null);
      } catch { setAkteStatus(null); }
    } catch (e) {
      toast.error('Mitarbeiter nicht gefunden');
      navigate('/admin/personal?tab=mitarbeiter');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    // Pflichtfeld: Name
    if (!(form.name || '').trim()) {
      toast.error('Name ist ein Pflichtfeld');
      return;
    }
    // E-Mail-Validierung: Leerzeichen entfernen + Format prüfen
    const emailTrimmed = (form.email || '').replace(/\s+/g, '').trim();
    if (form.email !== emailTrimmed) {
      setForm(p => ({ ...p, email: emailTrimmed }));
      toast.warning(`E-Mail hatte Leerzeichen und wurde bereinigt: ${emailTrimmed}`);
    }
    if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      toast.error('Ungültige E-Mail-Adresse');
      return;
    }
    setSaving(true);
    try {
      // Patch-Semantik: nur GEÄNDERTE Adressfelder senden.
      // Feld unverändert → gar nicht senden (kein stilles Überschreiben).
      // Feld bewusst geleert ('' statt vorigem Wert) → als Löschung senden.
      const payload = {
        name: form.name,
        role: form.role,
        location_ids: form.location_ids || [],
        email: emailTrimmed || undefined,
        base_updated_at: employee.updated_at || undefined,  // Schutz vor veraltetem Tab
      };
      const origStrasse = employee.strasse || '';
      const curStrasse = form.strasse || '';
      if (curStrasse !== origStrasse) payload.strasse = curStrasse;
      const origPlz = employee.plz_ort || '';
      const curPlz = form.plz_ort || '';
      if (curPlz !== origPlz) payload.plz_ort = curPlz;

      await axios.put(`${API}/admin/employees/${employeeId}`, payload, { headers });
      toast.success('Gespeichert');
      setEditing(false);
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('Konflikt: Der Datensatz wurde zwischenzeitlich an anderer Stelle geändert. Bitte neu laden.', { duration: 8000 });
      } else {
        const detail = e.response?.data?.detail;
        toast.error(detail || 'Speichern fehlgeschlagen – Änderungen wurden NICHT gespeichert. Bitte erneut versuchen.', { duration: 8000 });
      }
      // Formular bleibt im Bearbeitungsmodus, Eingaben bleiben erhalten.
    }
    finally { setSaving(false); }
  };

  const handleSaveHW = async () => {
    setSavingHW(true);
    try {
      await axios.put(`${API}/admin/employees/${employeeId}`, {
        chip_number:          hwForm.chip_number || null,
        key_number:           hwForm.key_number || null,
        received_hardware_at: hwForm.received_hardware_at || null,
        returned_hardware_at: hwForm.returned_hardware_at || null,
      }, { headers });
      toast.success('Hardware gespeichert');
      setEditingHW(false);
      await fetchAll();
      // Signatur-Modal öffnen wenn passend
      const hasIssuedNow = hwForm.received_hardware_at && (hwForm.chip_number || hwForm.key_number);
      const alreadySigAusgabe = employee?.signature_ausgabe;
      const hasReturnedNow = hwForm.returned_hardware_at;
      const alreadySigRueckgabe = employee?.signature_rueckgabe;
      if (hasReturnedNow && !alreadySigRueckgabe) {
        setHwSigEmpty(true);
        setHwSigModal({ type: 'rueckgabe', datum: hwForm.returned_hardware_at });
      } else if (hasIssuedNow && !alreadySigAusgabe) {
        setHwSigEmpty(true);
        setHwSigModal({ type: 'ausgabe', datum: hwForm.received_hardware_at });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSavingHW(false); }
  };

  const handleSaveHwSignature = async () => {
    if (!hwSigRef.current || hwSigRef.current.isEmpty()) {
      toast.error('Bitte unterschreiben');
      return;
    }
    setSavingHwSig(true);
    try {
      const sigData = hwSigRef.current.toDataURL('image/png');
      const field = hwSigModal.type === 'ausgabe' ? 'signature_ausgabe' : 'signature_rueckgabe';
      const datumField = hwSigModal.type === 'ausgabe' ? 'datum_unterschrift_ausgabe' : 'datum_unterschrift_rueckgabe';
      await axios.put(`${API}/admin/employees/${employeeId}`, {
        [field]: sigData,
        [datumField]: new Date().toLocaleDateString('de-DE'),
      }, { headers });
      toast.success(hwSigModal.type === 'ausgabe'
        ? 'Empfangsbestätigung gespeichert ✅'
        : 'Rückgabebestätigung gespeichert ✅');
      setHwSigModal(null);
      fetchAll();
    } catch (e) { toast.error('Fehler beim Speichern der Unterschrift'); }
    finally { setSavingHwSig(false); }
  };

  // Creates a retroactive Personalbogen for an existing employee
  const createRetroBogen = async (openModal = false) => {
    setCreatingBogen(true);
    try {
      const res = await axios.post(`${API}/personalbogen`, { employee_id: employeeId }, { headers });
      const bogenData = res.data;
      const publicUrl = `${window.location.origin}/onboarding/${bogenData.token}`;
      if (openModal) {
        // Admin fills it in manually
        setEditRetroModal(bogenData);
        fetchAll();
      } else {
        // Show link + PIN for employee to fill in themselves
        setRetroBogenLink({ url: publicUrl, pin: bogenData.pin_code, bogenId: bogenData.id });
        fetchAll();
      }
      toast.success('Personalbogen erstellt');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Erstellen'); }
    finally { setCreatingBogen(false); }
  };

  const handleResetPw = async () => {
    setResetPwLoading(true);
    try {
      const res = await axios.post(`${API}/admin/employees/${employeeId}/reset-password`, {}, { headers });
      setNewPassword(res.data.temp_password || res.data.new_password || '');
      if (res.data.email_sent === false) {
        // PIN wurde gesetzt, aber E-Mail-Versand ist fehlgeschlagen
        toast.warning(
          `PIN gesetzt — E-Mail konnte NICHT versendet werden (${res.data.email_error || 'Unbekannter Fehler'}). Bitte PIN manuell mitteilen: ${res.data.pin}`,
          { duration: 8000 }
        );
      } else if (res.data.email_sent === true) {
        toast.success(`Zugangsdaten an ${employee?.email} gesendet`);
      } else {
        // Älteres Backend ohne email_sent-Flag
        toast.success('Passwort zurückgesetzt und per E-Mail gesendet');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setResetPwLoading(false); }
  };

  const downloadPDF = async (url, filename) => {
    try {
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      a.download = filename; a.click();
    } catch { toast.error('PDF-Fehler'); }
  };

  const formatDt = (iso) => {
    if (!iso) return '–';
    try { return new Date(iso).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
    catch { return '–'; }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    </DashboardLayout>
  );

  if (!employee) return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/personal?tab=mitarbeiter')} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Button>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
          <h2 className="text-lg font-semibold text-foreground">Mitarbeiter nicht gefunden</h2>
          <p className="text-sm text-muted-foreground mt-1">Der Datensatz existiert nicht oder du hast keinen Zugriff.</p>
        </div>
      </div>
    </DashboardLayout>
  );

  const displayName = employee.name || employee.email;
  const bogenName = bogen ? `${(bogen.personal?.vorname || '')} ${(bogen.personal?.familienname || '')}`.trim() : null;

  // ── RV-Befreiung: Personalbogen-RV (System B) hat Vorrang vor Contract-RV (System A) ──
  const pbRvRequired    = !!bogen?.rv_required;
  const pbRvEmployeeDone = !!bogen?.rv_employee_done;
  const pbRvEmployerDone = !!bogen?.rv_employer_done;
  const pbRvComplete    = pbRvRequired && pbRvEmployeeDone && pbRvEmployerDone;
  // Personalbogen-RV steuert die Karte, sobald der Mitarbeiter dort (per Link/vor Ort) unterschrieben hat
  const usePersonalbogenRv = pbRvRequired && pbRvEmployeeDone;
  const pbRvSignedDate = bogen?.rv_antrag_an?.datum_unterschrift_an || null;

  const downloadPersonalbogenRvPdf = () => {
    const token = localStorage.getItem('token');
    fetch(`${API}/personalbogen/${bogen.id}/rv-pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `RV_Befreiungsantrag_${displayName}.pdf`;
        a.click();
      }).catch(() => toast.error('PDF nicht verfügbar'));
  };

  const handleSignRvPb = async () => {
    if (!rvPbSigRef.current || rvPbSigRef.current.isEmpty()) return;
    setRvPbSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await axios.put(
        `${API}/personalbogen/${bogen.id}/admin-rv-complete`,
        {
          signature_ag: rvPbSigRef.current.toDataURL('image/png'),
          datum_unterschrift_ag: today,
          eingangsdatum: today,
        },
        { headers }
      );
      toast.success('RV-Befreiung als Arbeitgeber gegengezeichnet');
      setShowRvSignPb(false);
      setRvPbSigEmpty(true);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Gegenzeichnen');
    } finally { setRvPbSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        {/* Back + Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/personal?tab=mitarbeiter')} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-3 flex-1">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
              employee.role === 'admin' ? 'bg-blue-500' : employee.role === 'stationsleitung' ? 'bg-violet-500' : 'bg-gray-400'
            }`}>
              {displayName[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{displayName}</h1>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[employee.role] || 'bg-gray-100 text-gray-700'}`}>
                  {ROLE_LABELS[employee.role] || employee.role}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${employee.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {employee.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Akte-Navigation (Tabs) */}
        <div className="flex flex-wrap gap-1.5 border-b border-border pb-3" data-testid="akte-tab-nav">
          {AKTE_TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`akte-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted'}`}>
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {activeTab === 'personaldaten' && (<>
        {/* ── PERSONALDATEN (Neuer Block) ──────────────────────────────────── */}
        <Card className="shadow-sm border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-primary" />
              Personaldaten
              {bogen && (
                <span className="text-xs font-normal text-muted-foreground">
                  · {bogen.status === 'approved' ? 'abgeschlossen' : bogen.status === 'submitted' ? 'ausgefüllt' : 'in Bearbeitung'}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Personalbogen anlegen wenn noch keiner existiert */}
            {!bogen && (
              <div className="flex items-center gap-3 mb-4 p-3 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20">
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Kein Personalbogen verknüpft</p>
                  <p className="text-xs text-muted-foreground">SV-Nr., Steuer-ID, Adresse etc. fehlen noch</p>
                </div>
                <Button size="sm" onClick={() => setShowBogenWizard(true)} className="gap-1.5 shrink-0" data-testid="create-bogen-wizard-btn">
                  <FileText className="h-3.5 w-3.5" /> Anlegen
                </Button>
              </div>
            )}
            {/* PIN-Code (nur Admin sichtbar) */}
            {bogen?.pin_code && (
              <div className="flex items-center gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                <Key className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                <span>PIN-Code des Mitarbeiters: <strong className="font-mono">{bogen.pin_code}</strong></span>
              </div>
            )}
            <PersonaldatenSection
              employee={employee}
              headers={headers}
              onBeschaeftigungsWechsel={() => setShowBeschaeftigungsWechsel(true)}
            />
          </CardContent>
        </Card>

        {/* ── 1. Stammdaten ─────────────────────────────────────────────── */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => !editing && setShowStammdatenCard(v => !v)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Stammdaten</span>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                {!editing ? (
                  <Button variant="ghost" size="sm" onClick={() => { setShowStammdatenCard(true); setEditing(true); }} className="gap-1.5">
                    <Edit2 className="h-3.5 w-3.5" /> Bearbeiten
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" onClick={handleSave} disabled={saving}>
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern
                    </Button>
                  </div>
                )}
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showStammdatenCard ? 'rotate-180' : ''}`} />
              </div>
            </CardTitle>
          </CardHeader>
          {showStammdatenCard && (
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Name</Label>
                {editing ? (
                  <Input value={form.name || ''} onChange={f('name')} className="h-9" />
                ) : <p className="text-sm font-medium">{employee.name || '–'}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">E-Mail (Login-Adresse)</Label>
                {editing ? (
                  <div>
                    <Input
                      value={form.email || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(p => ({ ...p, email: val }));
                      }}
                      onBlur={e => {
                        const trimmed = e.target.value.replace(/\s+/g, '').trim();
                        if (trimmed !== e.target.value) {
                          setForm(p => ({ ...p, email: trimmed }));
                          toast.warning('Leerzeichen in E-Mail entfernt');
                        }
                      }}
                      className={`h-9 ${form.email && /\s/.test(form.email) ? 'border-red-400' : ''}`}
                      placeholder="name@domain.de"
                      data-testid="edit-employee-email"
                    />
                    {form.email && /\s/.test(form.email) && (
                      <p className="text-xs text-red-500 mt-0.5">E-Mail enthält Leerzeichen!</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm">{employee.email}</p>
                    {employee.email && /\s/.test(employee.email) && (
                      <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">Leerzeichen!</span>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Rolle</Label>
                {editing ? (
                  <select value={form.role || ''} onChange={f('role')} className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                    {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'superadmin').map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                ) : <p className="text-sm">{ROLE_LABELS[employee.role] || employee.role}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Erstellt am</Label>
                <p className="text-sm">{formatDt(employee.created_at)}</p>
              </div>
            </div>

            {/* Adresse */}
            <div className="grid grid-cols-2 gap-4 pt-1 border-t border-border/50">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Straße + Hausnr.</Label>
                {editing ? (
                  <Input value={form.strasse || ''} onChange={f('strasse')} className="h-9" placeholder="z.B. Musterstr. 5" data-testid="employee-strasse-input" />
                ) : employee.strasse ? (
                  <p className="text-sm" data-testid="employee-strasse-value">{employee.strasse}</p>
                ) : employee.strasse_projected ? (
                  <p className="text-sm text-muted-foreground italic" data-testid="employee-strasse-projected">{employee.strasse_projected} <span className="not-italic text-[10px] text-amber-600 dark:text-amber-400">(aus Personalbogen)</span></p>
                ) : <p className="text-sm"><span className="text-muted-foreground text-xs">–</span></p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">PLZ / Ort</Label>
                {editing ? (
                  <Input value={form.plz_ort || ''} onChange={f('plz_ort')} className="h-9" placeholder="z.B. 70734 Fellbach" data-testid="employee-plzort-input" />
                ) : employee.plz_ort ? (
                  <p className="text-sm" data-testid="employee-plzort-value">{employee.plz_ort}</p>
                ) : employee.plz_ort_projected ? (
                  <p className="text-sm text-muted-foreground italic" data-testid="employee-plzort-projected">{employee.plz_ort_projected} <span className="not-italic text-[10px] text-amber-600 dark:text-amber-400">(aus Personalbogen)</span></p>
                ) : <p className="text-sm"><span className="text-muted-foreground text-xs">–</span></p>}
              </div>
            </div>

            {/* Passwort zurücksetzen */}
            <div className="border-t pt-3 flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleResetPw} disabled={resetPwLoading} className="gap-1.5">
                {resetPwLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Key className="h-3.5 w-3.5" />}
                Passwort zurücksetzen
              </Button>
              {newPassword && (
                <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <p className="text-xs">Neues Passwort: <strong className="font-mono">{newPassword}</strong></p>
                  <button onClick={() => setNewPassword('')}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
                </div>
              )}
            </div>
          </CardContent>
          )} {/* end Stammdaten accordion */}
        </Card>

        </>)}

        {activeTab === 'ausstattung' && (<>
        {/* ── 2. Hardware & Schlüssel ─── module: employee_hardware (Business) ── */}
        {withUpgrade('employee_hardware', 'Hardware & Schlüssel', (
        <Card className="shadow-sm">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => !editingHW && setShowHardware(v => !v)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" /> Hardware & Schlüssel
                {!employee.chip_number && !employee.key_number && !employee.received_hardware_at && (
                  <span className="text-xs text-muted-foreground font-normal">(keine Einträge)</span>
                )}
                {(employee.chip_number || employee.key_number || employee.received_hardware_at) && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-normal">vorhanden</span>
                )}
              </span>
              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                {!editingHW ? (
                  <Button variant="ghost" size="sm" onClick={() => { setShowHardware(true); setEditingHW(true); }} className="gap-1.5">
                    <Edit2 className="h-3.5 w-3.5" /> Bearbeiten
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingHW(false); setHwForm({ chip_number: employee.chip_number||'', key_number: employee.key_number||'', received_hardware_at: employee.received_hardware_at||'', returned_hardware_at: employee.returned_hardware_at||'' }); }}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" onClick={handleSaveHW} disabled={savingHW}>
                      {savingHW ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern
                    </Button>
                  </div>
                )}
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showHardware ? 'rotate-180' : ''}`} />
              </div>
            </CardTitle>
          </CardHeader>
          {showHardware && (
          <CardContent className="space-y-3">
            {/* Status-Badge */}
            {(() => {
              const hasReceived = employee.received_hardware_at;
              const hasReturned = employee.returned_hardware_at;
              if (!hasReceived) return (
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2 w-fit">
                  <Package className="h-3.5 w-3.5" /> Noch nicht ausgegeben
                </div>
              );
              if (hasReturned) return (
                <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 w-fit dark:bg-green-900/20 dark:border-green-800">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Zurückgegeben am {formatDate(hasReturned)}
                </div>
              );
              return (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit dark:bg-amber-900/20 dark:border-amber-800">
                  <Key className="h-3.5 w-3.5" /> Ausgegeben seit {formatDate(hasReceived)}
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-4">
              {/* Chip-Nummer */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Chip-Nummer</Label>
                {editingHW
                  ? <Input value={hwForm.chip_number} onChange={e => setHwForm(p => ({...p, chip_number: e.target.value}))} placeholder="z.B. 4812" className="h-9" data-testid="chip-number-input" />
                  : <p className="text-sm font-medium font-mono">{employee.chip_number || <span className="text-muted-foreground">–</span>}</p>
                }
              </div>

              {/* Schlüssel-Nummer */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Schlüssel-Nummer</Label>
                {editingHW
                  ? <Input value={hwForm.key_number} onChange={e => setHwForm(p => ({...p, key_number: e.target.value}))} placeholder="z.B. S-007" className="h-9" data-testid="key-number-input" />
                  : <p className="text-sm font-medium font-mono">{employee.key_number || <span className="text-muted-foreground">–</span>}</p>
                }
              </div>

              {/* Ausgabe-Datum */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Ausgabe am</Label>
                {editingHW
                  ? <Input type="date" value={hwForm.received_hardware_at} onChange={e => setHwForm(p => ({...p, received_hardware_at: e.target.value}))} className="h-9" data-testid="received-hardware-input" />
                  : <p className="text-sm font-medium">{formatDate(employee.received_hardware_at)}</p>
                }
              </div>

              {/* Rückgabe-Datum */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Rückgabe am</Label>
                {editingHW
                  ? <Input type="date" value={hwForm.returned_hardware_at} onChange={e => setHwForm(p => ({...p, returned_hardware_at: e.target.value}))} className="h-9" data-testid="returned-hardware-input" />
                  : <p className="text-sm font-medium">{formatDate(employee.returned_hardware_at)}</p>
                }
              </div>
            </div>

            {/* Unterschriften-Anzeige (nur im Ansichtsmodus) */}
            {!editingHW && (
              <div className="border-t pt-4 mt-2 space-y-3">
                {/* Ausgabe-Unterschrift */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <PenLine className="h-3.5 w-3.5" />
                    Empfangsbestätigung (Mitarbeiter)
                  </Label>
                  {employee.signature_ausgabe ? (
                    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-center gap-4">
                      <img src={employee.signature_ausgabe} alt="Unterschrift Ausgabe" className="h-12 object-contain border rounded bg-white" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Empfang bestätigt</p>
                        <p className="text-xs text-amber-700 dark:text-amber-400">{employee.datum_unterschrift_ausgabe || ''}</p>
                      </div>
                      <button onClick={() => { setHwSigEmpty(true); setHwSigModal({ type: 'ausgabe', datum: employee.received_hardware_at }); }}
                        className="ml-auto text-xs text-muted-foreground hover:text-primary underline">Neu unterschreiben</button>
                    </div>
                  ) : employee.received_hardware_at ? (
                    <button onClick={() => { setHwSigEmpty(true); setHwSigModal({ type: 'ausgabe', datum: employee.received_hardware_at }); }}
                      className="flex items-center gap-2 text-xs border border-dashed border-amber-300 rounded-xl px-4 py-2.5 text-amber-700 hover:bg-amber-50 transition-colors w-full"
                      data-testid="sign-ausgabe-btn">
                      <PenLine className="h-3.5 w-3.5" /> Empfang unterschreiben lassen
                    </button>
                  ) : null}
                </div>

                {/* Rückgabe-Unterschrift */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <PenLine className="h-3.5 w-3.5" />
                    Rückgabebestätigung (Arbeitgeber)
                  </Label>
                  {employee.signature_rueckgabe ? (
                    <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl p-3 flex items-center gap-4">
                      <img src={employee.signature_rueckgabe} alt="Unterschrift Rückgabe" className="h-12 object-contain border rounded bg-white" />
                      <div>
                        <p className="text-xs font-semibold text-green-800 dark:text-green-300">Rückgabe bestätigt</p>
                        <p className="text-xs text-green-700 dark:text-green-400">{employee.datum_unterschrift_rueckgabe || ''}</p>
                      </div>
                    </div>
                  ) : employee.returned_hardware_at ? (
                    <button onClick={() => { setHwSigEmpty(true); setHwSigModal({ type: 'rueckgabe', datum: employee.returned_hardware_at }); }}
                      className="flex items-center gap-2 text-xs border border-dashed border-green-300 rounded-xl px-4 py-2.5 text-green-700 hover:bg-green-50 transition-colors w-full"
                      data-testid="sign-rueckgabe-btn">
                      <PenLine className="h-3.5 w-3.5" /> Rückgabe bestätigen (AG unterschreibt)
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </CardContent>
          )} {/* end showHardware accordion */}
        </Card>
        ))} {/* end employee_hardware */}

        </>)}

        {activeTab === 'beschaeftigung' && (<>
        {/* ── Aktuelle Beschäftigung + Wechsel-Einstieg (geschützter Mitarbeiterlink-Flow) ── */}
        <Card className="shadow-sm border-primary/20" data-testid="beschaeftigung-current-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-primary" />
                Aktuelle Beschäftigung
              </span>
              {bogen && (
                <Button variant="outline" size="sm"
                  onClick={() => setShowBeschaeftigungsWechsel(true)}
                  className="gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
                  data-testid="change-employment-from-beschaeftigung-btn">
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                  Beschäftigungsart ändern
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!bogen ? (
              <p className="text-sm text-muted-foreground">Noch kein Personalbogen verknüpft – Beschäftigungsdaten erscheinen nach dem Anlegen (Tab „Personaldaten").</p>
            ) : (() => {
              const ag = bogen.arbeitgeber || {};
              const artRaw = ag.beschaeftigungsart || ag.arbeitszeit;
              const art = {vollzeit:'Vollzeit',teilzeit:'Teilzeit',minijob:'Minijob',werkstudent:'Werkstudent'}[artRaw] || artRaw || '–';
              const rows = [
                ['Beschäftigungsart', art],
                ['Stundenlohn', ag.stundenlohn ? `${ag.stundenlohn} €/Std.` : '–'],
                ['Stunden/Woche', ag.stunden_pro_woche || '–'],
                ['Stunden/Monat', ag.stunden_pro_monat || '–'],
                ['Eintrittsdatum', ag.eintrittsdatum || '–'],
                ['Beschäftigung wirksam ab', ag.beschaeftigung_wirksam_ab || ag.eintrittsdatum || '–'],
              ];
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                  {rows.map(([label, val]) => (
                    <div key={label} className="min-w-0" data-testid={`beschaeftigung-field-${label}`}>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                      <div className="text-sm font-medium truncate">{val}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {bogen?.scheduled_employment_change && (
              <div className="mt-4 flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg px-3 py-2" data-testid="beschaeftigung-scheduled-hint">
                <Clock className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  Geplanter Wechsel zu <strong>{({vollzeit:'Vollzeit',teilzeit:'Teilzeit',minijob:'Minijob',werkstudent:'Werkstudent'}[bogen.scheduled_employment_change.neue_beschaeftigungsart] || bogen.scheduled_employment_change.neue_beschaeftigungsart)}</strong> ab <strong>{bogen.scheduled_employment_change.wirksam_ab}</strong> – wird am Gültigkeitsdatum automatisch übernommen.
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── 3. Beschäftigungshistorie — eigenes Akkordeon (standardmäßig eingeklappt) ── */}
        {bogen?.employment_history?.length > 0 && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3 cursor-pointer select-none"
              onClick={() => setShowPersonalbogenCard(v => !v)}>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4 text-primary" />
                  Beschäftigungshistorie
                  <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-normal">
                    {bogen.employment_history.length} Eintr{bogen.employment_history.length === 1 ? 'ag' : 'äge'}
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showPersonalbogenCard ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CardHeader>
            {showPersonalbogenCard && (
            <CardContent>
              <div className="space-y-1.5">
                {[...bogen.employment_history].reverse().map((h, i) => (
                  <div key={h.id || i} className="flex items-center gap-3 text-xs bg-muted/30 rounded-lg px-3 py-2.5">
                    <div className="w-2 h-2 rounded-full bg-primary/40 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">
                        {{vollzeit:'Vollzeit',teilzeit:'Teilzeit',minijob:'Minijob',werkstudent:'Werkstudent'}[h.beschaeftigungsart] || h.beschaeftigungsart}
                      </span>
                      {h.stundenlohn && <span className="text-muted-foreground ml-2">{h.stundenlohn} €/Std.</span>}
                      {h.monatslohn && <span className="text-muted-foreground ml-2">{h.monatslohn} €/Monat</span>}
                    </div>
                    <div className="text-muted-foreground shrink-0 text-right">
                      {h.eintrittsdatum && <span>{h.eintrittsdatum}</span>}
                      {h.wirksam_bis && <span className="ml-1">→ {h.wirksam_bis}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
            )}
          </Card>
        )}

        </>)}

        {activeTab === 'dokumente' && (<>
        {/* ── 4. Verträge ─── module: employee_contracts (Business) ── */}
        {withUpgrade('employee_contracts', 'Arbeitsverträge', (
        <Card className="shadow-sm">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setShowArbeitsvertragCard(v => !v)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" /> Arbeitsvertrag
                {signing && <span className="text-xs font-normal text-muted-foreground">· {signing.template_name}</span>}
                {!signing && <span className="text-xs font-normal text-muted-foreground">(kein Vertrag)</span>}
              </span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showArbeitsvertragCard ? 'rotate-180' : ''}`} />
            </CardTitle>
          </CardHeader>
          {showArbeitsvertragCard && (
          <CardContent>
            {!signing ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <Shield className="h-8 w-8 mx-auto mb-2 opacity-30" />
                Kein Vertrag verknüpft
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm font-medium">{signing.template_name}</p>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button variant="outline" size="sm" className="gap-1.5"
                      onClick={() => downloadPDF(`${API}/contracts/signing/${signing.id}/pdf`, `Vertrag_${displayName}.pdf`)}>
                      <FileDown className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">PDF</span>
                    </Button>
                    {/* Signierlink wenn AN noch nicht unterschrieben hat */}
                    {(!signing.signature_an || signing.signature_an === 'already_signed') && (
                      <Button variant="outline" size="sm"
                        className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        disabled={generatingContractLink}
                        onClick={async () => {
                          setGeneratingContractLink(true);
                          try {
                            const res = await axios.post(
                              `${API}/contracts/signing/${signing.id}/generate-signing-link`, {}, { headers }
                            );
                            const fullUrl = `${window.location.origin}${res.data.url}`;
                            setContractLinkInfo({ url: fullUrl, pin_code: res.data.pin_code, name: displayName });
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Fehler beim Erstellen des Links');
                          } finally { setGeneratingContractLink(false); }
                        }}
                        data-testid="gen-signing-link-btn"
                      >
                        {generatingContractLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />}
                        <span className="hidden sm:inline">Signierlink für {displayName}</span>
                      </Button>
                    )}
                    {/* AG unterschreiben – wenn AN bereits unterschrieben hat */}
                    {signing.signature_an && !signing.signature_ag && (
                      <Button size="sm"
                        className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => { setAgSigEmpty(true); setShowSignAG(true); }}
                        data-testid="sign-ag-btn">
                        <Edit2 className="h-3.5 w-3.5" />
                        Als Arbeitgeber unterschreiben
                      </Button>
                    )}
                    {signing.status === 'complete' && (
                      <Button variant="outline" size="sm" className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/contracts/signing/${signing.id}/resend-completed-email`, {}, { headers });
                            toast.success('Vertrag per E-Mail gesendet!');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Fehler');
                          }
                        }}
                        data-testid="resend-contract-email-btn"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Erneut senden</span>
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Arbeitnehmer', sig: signing.signature_an, date: signing.signed_at_an },
                    { label: 'Arbeitgeber', sig: signing.signature_ag, date: signing.signed_at_ag },
                  ].map(item => (
                    <div key={item.label} className={`rounded-lg p-3 text-xs ${item.sig ? 'bg-green-50 border border-green-200' : 'bg-muted/40'}`}>
                      <p className="font-medium flex items-center gap-1.5">
                        {item.sig ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                        {item.label}
                      </p>
                      <p className="text-muted-foreground mt-0.5">{item.date ? formatDt(item.date) : 'Ausstehend'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
          )} {/* end Arbeitsvertrag accordion */}
        </Card>
        ))} {/* end employee_contracts */}

        {/* ── RV-Befreiungsantrag (nur bei Minijob + vorhandenem Signing) ─── */}
        {((bogen?.arbeitgeber?.arbeitszeit === 'minijob' ||
           bogen?.arbeitgeber?.beschaeftigungsart === 'minijob') || pbRvRequired || rvSigning) && (
          <Card className="shadow-sm border-violet-200 dark:border-violet-800" data-testid="rv-signing-card">
            <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setShowRvCard(v => !v)}>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-violet-600" />
                  RV-Befreiungsantrag (§ 6 SGB VI)
                  {usePersonalbogenRv ? (
                    pbRvComplete
                      ? <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-1.5 py-0.5 rounded-full font-medium" data-testid="rv-status-badge">unterschrieben</span>
                      : <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-medium" data-testid="rv-status-badge">Arbeitgeber offen</span>
                  ) : (<>
                    {rvSigning?.status === 'complete' && <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-1.5 py-0.5 rounded-full font-medium">unterschrieben</span>}
                    {rvSigning && rvSigning.status !== 'complete' && <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-medium">ausstehend</span>}
                    {!rvSigning && <span className="text-xs text-muted-foreground font-normal">(kein Antrag)</span>}
                  </>)}
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showRvCard ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CardHeader>
            {showRvCard && (
            <CardContent>
              {usePersonalbogenRv ? (
                pbRvComplete ? (
                  <div className="space-y-3" data-testid="rv-pb-complete">
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-3">
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                      <p className="text-sm font-medium text-green-800 dark:text-green-300">Befreiungsantrag vollständig unterschrieben (per Personalbogen-Link)</p>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1.5"
                      onClick={downloadPersonalbogenRvPdf} data-testid="rv-pb-pdf-btn">
                      <FileDown className="h-3.5 w-3.5" /> PDF herunterladen
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="rv-pb-employer-pending">
                    <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-3 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 shrink-0" />
                      <span className="text-blue-800 dark:text-blue-300">
                        Mitarbeiter hat per Personalbogen-Link{pbRvSignedDate ? ` am ${new Date(pbRvSignedDate).toLocaleDateString('de-DE')}` : ''} unterschrieben — jetzt als Arbeitgeber gegenzeichnen.
                      </span>
                    </div>
                    <Button size="sm"
                      className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white"
                      onClick={() => { setRvPbSigEmpty(true); setShowRvSignPb(true); }}
                      data-testid="rv-pb-sign-ag-btn">
                      <Edit2 className="h-3.5 w-3.5" /> RV gegenzeichnen
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 ml-2"
                      onClick={downloadPersonalbogenRvPdf} data-testid="rv-pb-preview-pdf-btn">
                      <FileDown className="h-3.5 w-3.5" /> Vorschau-PDF
                    </Button>
                  </div>
                )
              ) : !rvSigning ? (
                <div className="text-center py-4 text-muted-foreground text-sm space-y-3">
                  <Shield className="h-7 w-7 mx-auto opacity-30" />
                  <p>Kein Befreiungsantrag vorhanden.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                    disabled={creatingRvSigning}
                    onClick={async () => {
                      setCreatingRvSigning(true);
                      try {
                        const res = await axios.post(
                          `${API}/contracts/rv-signing/create`,
                          { employee_id: employeeId },
                          { headers }
                        );
                        toast.success('RV-Befreiungsantrag erstellt');
                        fetchAll(); // RV-Karte aktualisieren
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
                      } finally { setCreatingRvSigning(false); }
                    }}
                    data-testid="create-rv-signing-btn"
                  >
                    {creatingRvSigning
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Shield className="h-3.5 w-3.5" />}
                    RV-Antrag jetzt erstellen
                  </Button>
                </div>
              ) : rvSigning.status === 'complete' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-3">
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">Befreiungsantrag vollständig unterschrieben</p>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5"
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      fetch(`${API}/contracts/signing/${rvSigning.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.blob()).then(blob => {
                          const a = document.createElement('a');
                          a.href = URL.createObjectURL(blob);
                          a.download = `RV_Befreiungsantrag_${displayName}.pdf`;
                          a.click();
                        }).catch(() => toast.error('PDF nicht verfügbar'));
                    }}
                    data-testid="rv-pdf-btn">
                    <FileDown className="h-3.5 w-3.5" /> PDF herunterladen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Status */}
                  <div className={`flex items-center gap-2 rounded-xl p-3 border text-sm ${
                    rvSigning.status === 'signed_ag'
                      ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200'
                      : 'bg-muted/30 border-border'
                  }`}>
                    <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                    <span className="font-medium">Mitarbeiter-Unterschrift ausstehend</span>
                  </div>

                  {/* PIN für Mitarbeiter */}
                  {rvSigning.pin_code && (
                    <div className="flex items-center gap-2 bg-white dark:bg-gray-900/40 border rounded-lg px-3 py-2">
                      <Key className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground">PIN für Mitarbeiter:</span>
                      <span className="font-mono font-bold text-sm tracking-widest" data-testid="rv-pin">{rvSigning.pin_code}</span>
                      <span className="text-xs text-muted-foreground ml-auto">(mündlich mitteilen)</span>
                    </div>
                  )}

                  {/* Aktions-Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm"
                      onClick={() => {
                        if (rvSigning.signing_token) {
                          window.open(`${window.location.origin}/vertrag/${rvSigning.signing_token}`, '_blank', 'noopener');
                        } else {
                          toast.error('Signierlink nicht verfügbar — bitte Beschäftigungswechsel erneut durchführen');
                        }
                      }}
                      className="gap-1.5 text-xs"
                      data-testid="rv-vor-ort-btn">
                      <Mail className="h-3.5 w-3.5" />
                      Vor Ort unterschreiben
                    </Button>
                    <Button variant="outline" size="sm"
                      disabled={sendingRvEmail}
                      onClick={async () => {
                        setSendingRvEmail(true);
                        try {
                          await axios.post(`${API}/contracts/signing/${rvSigning.id}/send-signing-link-email`, {}, { headers });
                          toast.success(`Signierlink an ${displayName} gesendet`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Fehler beim Senden');
                        } finally { setSendingRvEmail(false); }
                      }}
                      className="gap-1.5 text-xs"
                      data-testid="rv-email-btn">
                      {sendingRvEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                      Per E-Mail senden
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <strong>Vor Ort:</strong> Mitarbeiter unterschreibt sofort auf diesem Gerät. &nbsp;
                    <strong>E-Mail:</strong> Link an Mitarbeiter, unterschreibt später remote.
                  </p>
                </div>
              )}
            </CardContent>
            )} {/* end RV accordion */}
          </Card>
        )}

        </>)}

        {activeTab === 'verlauf' && (<>
        {/* ── 5. Audit-Log ─────────────────────────────────────────────────── */}
        {signing?.audit_log?.length > 0 && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" /> Audit-Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {signing.audit_log.slice().reverse().map((entry, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs">
                    <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
                    <div>
                      <span className="text-foreground font-medium">{entry.action}</span>
                      <span className="text-muted-foreground ml-2">{entry.user_name}</span>
                      <span className="text-muted-foreground ml-2">{formatDt(entry.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        </>)}

        {activeTab === 'dokumente' && (<>
        {/* ── 6. Arbeitszeugnis ─── module: employee_certificates (Business) ── */}
        {withUpgrade('employee_certificates', 'Arbeitszeugnisse', (
        <Card className="shadow-sm border-violet-200 dark:border-violet-800">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setShowZeugnisSection(v => !v)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-violet-600" />
                Arbeitszeugnisse
                {zeugnisse.length > 0 && (
                  <span className="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 rounded-full font-medium">
                    {zeugnisse.length}
                  </span>
                )}
                {zeugnisse.length === 0 && <span className="text-xs text-muted-foreground font-normal">(keine)</span>}
              </span>
              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <Button
                  size="sm"
                  className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => { setEditingZeugnis(null); setShowZeugnis(true); }}
                  data-testid="create-zeugnis-btn"
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">KI-Zeugnis erstellen</span>
                  <span className="sm:hidden">Zeugnis</span>
                </Button>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showZeugnisSection ? 'rotate-180' : ''}`} />
              </div>
            </CardTitle>
          </CardHeader>
          {showZeugnisSection && (
          <CardContent>
            {zeugnisse.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm space-y-1">
                <FileText className="h-8 w-8 mx-auto opacity-30" />
                <p className="text-xs">Noch kein Zeugnis erstellt.</p>
                <p className="text-xs text-muted-foreground">Standard: Gemini Flash (~0,5 Cent) | Premium: Claude Sonnet (~2 Cent)</p>
              </div>
            ) : (
              <div className="space-y-2">
                {zeugnisse.map(z => (
                  <div key={z.id} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/40 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-violet-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          Arbeitszeugnis · Note {z.gesamtnote}
                          {['', ' (sehr gut)', ' (gut)', ' (befriedigend)', ' (ausreichend)', ' (mangelhaft)'][z.gesamtnote] || ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(z.created_at).toLocaleDateString('de-DE')} · {z.model_used?.includes('claude') ? 'Claude' : 'Gemini'}
                          {z.updated_at && z.updated_at !== z.created_at && <span className="ml-1 text-violet-500">· bearbeitet</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="outline" size="sm" className="h-8 w-8 sm:w-auto sm:px-2.5 p-0 border-violet-200 text-violet-600 hover:bg-violet-50"
                        onClick={() => { setEditingZeugnis(z); setShowZeugnis(true); }}
                        data-testid={`edit-zeugnis-${z.id}`} title="Bearbeiten">
                        <Edit2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline ml-1 text-xs">Bearbeiten</span>
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 w-8 sm:w-auto sm:px-2.5 p-0"
                        disabled={downloadingZeugnis === z.id}
                        onClick={async () => {
                          setDownloadingZeugnis(z.id);
                          try {
                            const res = await axios.get(`${API}/arbeitszeugnis/${z.id}/pdf`, { headers, responseType: 'blob' });
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a'); a.href = url;
                            a.download = `Arbeitszeugnis_${displayName.replace(' ','_')}.pdf`; a.click();
                          } catch { toast.error('Download fehlgeschlagen'); }
                          finally { setDownloadingZeugnis(null); }
                        }}
                        data-testid={`download-zeugnis-${z.id}`} title="PDF herunterladen">
                        {downloadingZeugnis === z.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                        <span className="hidden sm:inline ml-1 text-xs">PDF</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          )} {/* end Zeugnis accordion */}
        </Card>

        ))} {/* end employee_certificates */}

        {/* ── 7. Abmahnungen ─── module: employee_warnings (Business) ── */}
        {withUpgrade('employee_warnings', 'Abmahnungen', (
        <Card className="shadow-sm border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setShowAbmahnungSection(v => !v)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Abmahnungen
                {abmahnungen.length > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-medium">
                    {abmahnungen.length}
                  </span>
                )}
                {abmahnungen.length === 0 && <span className="text-xs text-muted-foreground font-normal">(keine)</span>}
              </span>
              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <Button size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white h-8 px-2 sm:px-3"
                  onClick={() => setShowAbmahnung(true)} data-testid="create-abmahnung-btn">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Abmahnung erstellen</span>
                </Button>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showAbmahnungSection ? 'rotate-180' : ''}`} />
              </div>
            </CardTitle>
          </CardHeader>
          {showAbmahnungSection && (
          <CardContent>
            {abmahnungen.length === 0 ? (
              <p className="text-xs text-center text-muted-foreground py-3">Keine Abmahnungen in der Akte.</p>
            ) : (
              <div className="space-y-2">
                {abmahnungen.map(ab => (
                  <div key={ab.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{ab.grund}</p>
                      <p className="text-xs text-muted-foreground">{ab.vorfall_datum} · Erstellt: {new Date(ab.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1 shrink-0 h-8 px-2 border-amber-300"
                      disabled={downloadingAbmahnung === ab.id}
                      onClick={async () => {
                        setDownloadingAbmahnung(ab.id);
                        try {
                          const res = await axios.get(`${API}/abmahnung/${ab.id}/pdf`, { headers, responseType: 'blob' });
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `Abmahnung_${displayName.replace(' ','_')}.pdf`; a.click();
                        } catch { toast.error('PDF-Fehler'); }
                        finally { setDownloadingAbmahnung(null); }
                      }}
                      data-testid={`download-abmahnung-${ab.id}`}>
                      {downloadingAbmahnung === ab.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          )} {/* end Abmahnung accordion */}
        </Card>
        ))} {/* end employee_warnings */}

        {/* ── 8. Kündigung ─── module: employee_terminations (Business) ── */}
        {withUpgrade('employee_terminations', 'Kündigungsassistent & Aufhebungsvertrag', (
        <Card className="shadow-sm border-red-200 dark:border-red-900">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setShowKuendigungSection(v => !v)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-red-600" />
                Kündigung
                {kuendigungen.length > 0 && (
                  <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded-full font-medium">
                    Dokument vorhanden
                  </span>
                )}
                {kuendigungen.length === 0 && <span className="text-xs text-muted-foreground font-normal">(keine)</span>}
              </span>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <Button size="sm" variant="outline"
                  className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 px-2 sm:px-3"
                  onClick={() => setShowKuendigungsAssistent(true)}
                  data-testid="kuendigungs-assistent-btn">
                  <Scale className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Kündigung erstellen</span>
                </Button>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showKuendigungSection ? 'rotate-180' : ''}`} />
              </div>
            </CardTitle>
          </CardHeader>
          {showKuendigungSection && (
          <CardContent>
            {kuendigungen.length === 0 ? (
              <div className="text-center py-3 space-y-1">
                <p className="text-xs text-muted-foreground">Noch kein Kündigungsdokument vorhanden.</p>
                <p className="text-xs text-muted-foreground">Inkl. §622 BGB Fristberechnung, Rückgabe-Checkliste + Zustellungsprotokoll.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {kuendigungen.map(k => {
                  const isAufhebung = k.source === 'aufhebungsvertrag';
                  const artLabel = isAufhebung ? 'Aufhebungsvertrag'
                    : k.kuendigungsart_assistent === 'fristlos_hilfsweise' ? 'Fristlos + Hilfsweise'
                    : k.kuendigungsart === 'fristlos' ? 'Fristlose Kündigung'
                    : 'Ordentliche Kündigung';
                  const isAssistent = k.source === 'assistent';
                  const pdfBase = isAufhebung
                    ? `${API}/kuendigung/aufhebungsvertrag/${k.id}`
                    : isAssistent ? `${API}/kuendigung/assistent/${k.id}` : `${API}/kuendigung/${k.id}`;
                  const videoRef = React.createRef();
                  const videoGalleryRef = React.createRef();
                  // Farb-Schema: Aufhebungsvertrag = blau, Kündigung = rot
                  const colorClass = isAufhebung
                    ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-900/10'
                    : 'border-red-200 bg-red-50/50 dark:bg-red-900/10';
                  const textColor = isAufhebung
                    ? 'text-blue-800 dark:text-blue-300'
                    : 'text-red-800 dark:text-red-300';
                  const btnColor = isAufhebung
                    ? 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    : 'border-red-300';
                  return (
                  <div key={k.id} className={`rounded-lg border overflow-hidden ${colorClass}`}>
                    {/* Header */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium flex items-start gap-1.5 flex-wrap ${textColor}`}>
                            {isAufhebung && <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded font-semibold shrink-0">🤝 Aufhebungsvertrag</span>}
                            {isAssistent && !isAufhebung && <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold shrink-0">Assistent</span>}
                            <span>
                              {artLabel}
                              {isAufhebung && k.beendigungs_datum ? ` zum ${k.beendigungs_datum}` : ''}
                              {!isAufhebung && k.kuendigungs_datum ? ` zum ${k.kuendigungs_datum}` : ''}
                            </span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Erstellt: {new Date(k.created_at).toLocaleDateString('de-DE')}
                            {isAufhebung && k.abfindung && k.abfindung_betrag && (
                              <span className="ml-2 text-blue-600 font-medium">Abfindung: {Number(k.abfindung_betrag).toLocaleString('de-DE')} €</span>
                            )}
                            {k.video_nachweis_path && <span className="ml-2 text-green-600 font-medium">🎥 Video vorhanden</span>}
                          </p>
                        </div>
                        {/* Löschen-Button rechts oben */}
                        <button
                          onClick={async () => {
                            const label = isAufhebung ? 'Aufhebungsvertrag' : 'Kündigung';
                            if (!window.confirm(`${label} vom ${new Date(k.created_at).toLocaleDateString('de-DE')} wirklich löschen?`)) return;
                            try {
                              await axios.delete(`${API}/kuendigung/${k.id}`, { headers });
                              toast.success(`${label} gelöscht`);
                              fetchAll();
                            } catch { toast.error('Fehler beim Löschen'); }
                          }}
                          className={`shrink-0 p-1.5 rounded-lg transition-colors ${isAufhebung ? 'text-blue-400 hover:text-blue-600 hover:bg-blue-100' : 'text-red-400 hover:text-red-600 hover:bg-red-100'}`}
                          data-testid={`delete-kuendigung-${k.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {/* PDF-Buttons */}
                      <div className="flex flex-wrap gap-1.5">
                        {isAufhebung ? (
                          <Button variant="outline" size="sm" className={`h-8 px-2 text-xs ${btnColor}`}
                            onClick={async () => {
                              try {
                                const res = await axios.get(`${pdfBase}/pdf`, { headers, responseType: 'blob' });
                                const burl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                const a = document.createElement('a'); a.href = burl;
                                a.download = `Aufhebungsvertrag_${(k.mitarbeiter_name||'').replace(/ /g,'_')}.pdf`; a.click();
                              } catch { toast.error('PDF-Fehler'); }
                            }}>
                            📄 Aufhebungsvertrag PDF
                          </Button>
                        ) : (
                        (isAssistent ? ['pdf','pdf-protokoll','pdf-intern'] : ['schreiben','protokoll']).map(type => (
                          <Button key={type} variant="outline" size="sm" className={`h-8 px-2 text-xs ${btnColor}`}
                            onClick={async () => {
                              try {
                                const url = isAssistent ? `${pdfBase}/${type}` : `${pdfBase}/pdf-${type}`;
                                const res = await axios.get(url, { headers, responseType: 'blob' });
                                const burl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                const a = document.createElement('a'); a.href = burl;
                                a.download = `${type}_${displayName.replace(' ','_')}.pdf`; a.click();
                              } catch { toast.error('PDF-Fehler'); }
                            }}>
                            <FileDown className="h-3.5 w-3.5 mr-1" />
                            {type === 'pdf' || type === 'schreiben' ? 'Kündigungsschreiben' : type === 'pdf-protokoll' || type === 'protokoll' ? 'Protokoll' : 'Intern'}
                          </Button>
                        )))}
                      </div>
                    </div>

                    {/* Video-Nachweis – nur für Kündigung (nicht Aufhebungsvertrag) */}
                    {!isAufhebung && (isAssistent || k.id) && (
                      <div className="border-t border-red-200 bg-white dark:bg-gray-900/30 px-3 py-2">
                        {k.video_nachweis_path ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-green-700 dark:text-green-400 font-medium">
                                🎥 Video gespeichert · {k.video_nachweis_at ? new Date(k.video_nachweis_at).toLocaleString('de-DE') : ''}
                              </span>
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              {/* Ansehen: Token generieren + Video öffnen */}
                              <button onClick={async () => {
                                try {
                                  const res = await axios.post(`${API}/kuendigung/assistent/${k.id}/video/generate-token`, {}, { headers });
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/kuendigung/video/${res.data.token}`;
                                  window.open(url, '_blank');
                                  toast.success(`Freigabelink erstellt (gültig bis ${new Date(res.data.expires).toLocaleDateString('de-DE')})`);
                                  fetchAll();
                                } catch { toast.error('Fehler'); }
                              }} className="flex items-center gap-1 text-xs px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                                ▶ Ansehen
                              </button>
                              {/* QR im Protokoll: Token generieren → PDF neu laden */}
                              <button onClick={async () => {
                                try {
                                  await axios.post(`${API}/kuendigung/assistent/${k.id}/video/generate-token`, {}, { headers });
                                  toast.success('QR-Code wurde erstellt. Bitte Zustellungsprotokoll neu herunterladen.');
                                  fetchAll();
                                } catch { toast.error('Fehler'); }
                              }} className="flex items-center gap-1 text-xs px-2 py-1 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors">
                                QR-Code für Protokoll
                              </button>
                              {k.video_nachweis_token_active && (
                                <span className="text-[10px] text-green-600 flex items-center gap-0.5">
                                  ✓ Token aktiv bis {k.video_nachweis_token_expires ? new Date(k.video_nachweis_token_expires).toLocaleDateString('de-DE') : ''}
                                </span>
                              )}
                              <button onClick={async () => {
                                if (!window.confirm('Video-Nachweis löschen?')) return;
                                await axios.delete(`${API}/kuendigung/assistent/${k.id}/video`, { headers });
                                toast.success('Video gelöscht');
                                fetchAll();
                              }} className="ml-auto text-xs text-red-400 hover:text-red-600 underline">
                                Löschen
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">🎥 Video-Nachweis hinzufügen:</span>
                            <button type="button" onClick={() => videoRef.current?.click()}
                              className="flex items-center gap-1 text-xs px-2 py-1 border border-indigo-300 rounded-lg text-indigo-700 hover:bg-indigo-50 transition-colors">
                              Aufnehmen
                            </button>
                            <button type="button" onClick={() => videoGalleryRef.current?.click()}
                              className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
                              Hochladen
                            </button>
                            <input ref={videoRef} type="file" accept="video/*" capture="environment" className="hidden"
                              onChange={async e => {
                                const file = e.target.files?.[0]; if (!file) return;
                                const fd = new FormData(); fd.append('file', file);
                                try {
                                  await axios.post(`${API}/kuendigung/assistent/${k.id}/video`, fd, { headers });
                                  toast.success('🎥 Video-Nachweis gespeichert!');
                                  fetchAll();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                              }} />
                            <input ref={videoGalleryRef} type="file" accept="video/*" className="hidden"
                              onChange={async e => {
                                const file = e.target.files?.[0]; if (!file) return;
                                const fd = new FormData(); fd.append('file', file);
                                try {
                                  await axios.post(`${API}/kuendigung/assistent/${k.id}/video`, fd, { headers });
                                  toast.success('🎥 Video-Nachweis gespeichert!');
                                  fetchAll();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                              }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
          )} {/* end Kündigung accordion */}
        </Card>
        ))} {/* end employee_terminations */}

        </>)}

        {activeTab === 'uebersicht' && (<>
        {/* ── PERSONALAKTE-ÜBERSICHT ────────────────────────────────────── */}
        <PersonalakteOverview
          bogen={bogen}
          signing={signing}
          rvSigning={rvSigning}
          akteStatus={akteStatus}
          zeugnisse={zeugnisse}
          abmahnungen={abmahnungen}
          kuendigungen={kuendigungen}
          employeeId={employeeId}
          onOpenSection={(section) => {
            if (section === 'personalbogen')  { setActiveTab('personaldaten'); setShowPersonalbogenCard(true); }
            if (section === 'arbeitsvertrag') { setActiveTab('dokumente'); setShowArbeitsvertragCard(true); }
            if (section === 'rv')             { setActiveTab('dokumente'); setShowRvCard(true); }
            if (section === 'zeugnisse')      { setActiveTab('dokumente'); setShowZeugnisSection(true); }
            if (section === 'abmahnungen')    { setActiveTab('dokumente'); setShowAbmahnungSection(true); }
            if (section === 'kuendigung')     { setActiveTab('dokumente'); setShowKuendigungSection(true); }
          }}
          onCreateContract={() => navigate(`/admin/personal?tab=vertraege&employee=${employeeId}`)}
        />

        </>)}

        {activeTab === 'dokumente' && (
        <EmployeeDokumenteTab employeeId={employeeId} userRole={user?.role} />
        )}

        {activeTab === 'unterweisungen' && (
          <Card className="shadow-sm" data-testid="akte-unterweisungen-link">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" /> Unterweisungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Unterweisungen und Belehrungen werden zentral im Modul "Unterweisungen" verwaltet. Hier geht es direkt zur mitarbeiterbezogenen Uebersicht.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/admin/unterweisungen/mitarbeiter')} data-testid="akte-goto-unterweisungen-mitarbeiter">
                  <Shield className="h-3.5 w-3.5" /> Unterweisungen je Mitarbeiter
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/unterweisungen')} data-testid="akte-goto-unterweisungen">
                  Gesamtuebersicht oeffnen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* PersonalboqenWizard für Bestandsmitarbeiter */}
      <PersonalboqenWizard
        open={showBogenWizard}
        onClose={() => setShowBogenWizard(false)}
        onCreated={(newBogen) => {
          fetchAll();
          if (newBogen) setEditRetroModal(newBogen);
        }}
        locations={locations}
        existingEmployee={employee}
      />

      {/* Retroactive Personalbogen Admin-Fill Modal */}
      {editRetroModal && (
        <PersonalboqenEditModal
          open={!!editRetroModal}
          bogen={editRetroModal}
          onClose={() => setEditRetroModal(null)}
          onSaved={() => { setEditRetroModal(null); fetchAll(); }}
        />
      )}

      {/* KI-Arbeitszeugnis Modal */}
      <ArbeitszeugnisModal
        open={showZeugnis}
        onClose={() => { setShowZeugnis(false); setEditingZeugnis(null); fetchAll(); }}
        employee={employee}
        bogen={bogen}
        locations={locations}
        editingZeugnis={editingZeugnis}
      />

      {/* Abmahnung Modal */}
      <AbmahnungModal
        open={showAbmahnung}
        onClose={() => { setShowAbmahnung(false); fetchAll(); }}
        employee={employee}
        bogen={bogen}
        locations={locations}
      />

      {/* Kündigungsassistent */}
      <KuendigungsAssistent
        open={showKuendigungsAssistent}
        onClose={() => { setShowKuendigungsAssistent(false); fetchAll(); }}
        employee={employee}
        bogen={bogen}
        locations={locations}
        abmahnungenCount={abmahnungen.length}
      />

      {/* Beschäftigungsart wechseln */}
      <BeschaeftigungsWechselModal
        open={showBeschaeftigungsWechsel}
        onClose={() => setShowBeschaeftigungsWechsel(false)}
        employee={employee}
        bogen={bogen}
        onSaved={() => fetchAll({ silent: true })}
      />

      {/* ── AG UNTERSCHRIFT DIALOG ──────────────────────────────────── */}
      <Dialog open={showSignAG} onOpenChange={v => { if (!v) setShowSignAG(false); }}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-green-600" />
              Arbeitsvertrag gegenzeichnen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-3 text-sm">
              <p className="font-semibold text-green-800 dark:text-green-300">{displayName}</p>
              <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">{signing?.template_name}</p>
              {signing?.signed_at_an && (
                <p className="text-xs text-green-600 mt-1">
                  Mitarbeiter hat am {new Date(signing.signed_at_an).toLocaleDateString('de-DE')} unterschrieben.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Ihre Unterschrift als Arbeitgeber *</Label>
              <div className={`border-2 rounded-xl overflow-hidden bg-white dark:bg-gray-900 ${agSigEmpty ? 'border-border' : 'border-green-400'}`}>
                <SignaturePad ref={agSigRef} penColor="#1E40AF" height={180}
                  onEnd={() => setAgSigEmpty(false)} />
              </div>
              <button type="button" onClick={() => { agSigRef.current?.clear(); setAgSigEmpty(true); }}
                className="text-xs text-muted-foreground hover:text-foreground underline">
                Zurücksetzen
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Nach der Unterschrift wird der Vertrag automatisch per E-Mail an den Mitarbeiter gesendet.
            </p>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSignAG(false)} className="w-full sm:w-auto">
              Abbrechen
            </Button>
            <Button
              disabled={agSaving || agSigEmpty}
              className="gap-2 w-full sm:w-auto bg-green-600 hover:bg-green-700"
              onClick={async () => {
                if (!agSigRef.current || agSigRef.current.isEmpty()) return;
                setAgSaving(true);
                try {
                  const sigData = agSigRef.current.toDataURL('image/png');
                  await axios.post(
                    `${API}/contracts/signing/${signing.id}/sign-ag`,
                    { signature_ag: sigData, datum: new Date().toLocaleDateString('de-DE'), ort: '' },
                    { headers }
                  );
                  toast.success('✅ Gegengezeichnet! Vertrag wird automatisch per E-Mail versendet.');
                  setShowSignAG(false);
                  fetchAll();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Fehler beim Unterschreiben');
                } finally { setAgSaving(false); }
              }}
              data-testid="confirm-sign-ag-btn">
              {agSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Jetzt gegenzeichnen & E-Mail senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── RV-BEFREIUNG ARBEITGEBER GEGENZEICHNEN (Personalbogen-RV) ──── */}
      <Dialog open={showRvSignPb} onOpenChange={v => { if (!v) setShowRvSignPb(false); }}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-violet-600" />
              RV-Befreiung als Arbeitgeber gegenzeichnen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-3 text-sm">
              <p className="font-semibold text-violet-800 dark:text-violet-300">{displayName}</p>
              <p className="text-xs text-violet-700 dark:text-violet-400 mt-0.5">
                Antrag auf Befreiung von der Rentenversicherungspflicht (§ 6 SGB VI)
              </p>
              {pbRvSignedDate && (
                <p className="text-xs text-violet-600 mt-1">
                  Mitarbeiter hat per Link am {new Date(pbRvSignedDate).toLocaleDateString('de-DE')} unterschrieben.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Ihre Unterschrift als Arbeitgeber *</Label>
              <div className={`border-2 rounded-xl overflow-hidden bg-white dark:bg-gray-900 ${rvPbSigEmpty ? 'border-border' : 'border-violet-400'}`}>
                <SignaturePad ref={rvPbSigRef} penColor="#6D28D9" height={180}
                  onEnd={() => setRvPbSigEmpty(false)} />
              </div>
              <button type="button" onClick={() => { rvPbSigRef.current?.clear(); setRvPbSigEmpty(true); }}
                className="text-xs text-muted-foreground hover:text-foreground underline">
                Zurücksetzen
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Bestätigt den Eingang beim Arbeitgeber. Die Unterschrift des Mitarbeiters bleibt erhalten.
            </p>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowRvSignPb(false)} className="w-full sm:w-auto">
              Abbrechen
            </Button>
            <Button
              disabled={rvPbSaving || rvPbSigEmpty}
              className="gap-2 w-full sm:w-auto bg-violet-600 hover:bg-violet-700"
              onClick={handleSignRvPb}
              data-testid="rv-pb-sign-ag-submit">
              {rvPbSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Jetzt gegenzeichnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signierlink + PIN Dialog */}
      <Dialog open={!!contractLinkInfo} onOpenChange={() => setContractLinkInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-primary" />
              Signierlink für {contractLinkInfo?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* PIN */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">
                PIN-Code für den Mitarbeiter
              </p>
              <p className="text-3xl font-bold font-mono tracking-[0.4em] text-amber-800 dark:text-amber-300">
                {contractLinkInfo?.pin_code || '–'}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">
                Den PIN-Code dem Mitarbeiter <strong>persönlich</strong> mitteilen (nicht per E-Mail, DSGVO).
                Das Dokument lässt sich nur damit öffnen.
              </p>
            </div>
            {/* Link */}
            <div className="space-y-1.5">
              <Label className="text-sm">Signierlink</Label>
              <div className="flex gap-2">
                <input readOnly value={contractLinkInfo?.url || ''}
                  className="flex-1 h-9 rounded-md border bg-muted px-3 text-xs font-mono text-muted-foreground" />
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(contractLinkInfo?.url || '');
                  toast.success('Link kopiert!');
                }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* WhatsApp */}
            <Button variant="outline" className="w-full gap-2 border-green-300 text-green-700 hover:bg-green-50"
              onClick={() => {
                const msg = `Hallo ${contractLinkInfo?.name}! 👋\n\nBitte unterschreibe deinen Arbeitsvertrag:\n\n🔗 ${contractLinkInfo?.url}\n\nDen PIN-Code erhältst du von mir persönlich.`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              }}>
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Per WhatsApp senden (Link, ohne PIN)
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Nach beidseitiger Unterschrift erhält der Mitarbeiter automatisch das verschlüsselte PDF per E-Mail.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractLinkInfo(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Hardware Signatur-Modal ───────────────────────────────────── */}
      <Dialog open={!!hwSigModal} onOpenChange={() => setHwSigModal(null)}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5 text-amber-600" />
              {hwSigModal?.type === 'ausgabe'
                ? 'Empfang bestätigen – Mitarbeiter unterschreibt'
                : 'Rückgabe bestätigen – Arbeitgeber unterschreibt'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className={`rounded-xl p-3 text-sm ${hwSigModal?.type === 'ausgabe' ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200' : 'bg-green-50 dark:bg-green-900/20 border border-green-200'}`}>
              {hwSigModal?.type === 'ausgabe' ? (
                <p className="text-amber-800 dark:text-amber-300">
                  <strong>{employee?.name}</strong> bestätigt den Empfang von:<br />
                  {employee?.chip_number && <span>🔑 Chip: <strong>{employee.chip_number}</strong>&nbsp;&nbsp;</span>}
                  {employee?.key_number && <span>🗝 Schlüssel: <strong>{employee.key_number}</strong></span>}
                </p>
              ) : (
                <p className="text-green-800 dark:text-green-300">
                  Arbeitgeber bestätigt die Rückgabe aller Hardware von <strong>{employee?.name}</strong>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Unterschrift *</Label>
              <div className={`border-2 rounded-xl overflow-hidden bg-white dark:bg-gray-900 ${hwSigEmpty ? 'border-border' : 'border-green-400'}`}>
                <SignaturePad
                  ref={hwSigRef}
                  penColor={hwSigModal?.type === 'ausgabe' ? '#D97706' : '#16A34A'}
                  height={180}
                  onEnd={() => setHwSigEmpty(false)}
                />
              </div>
              <button type="button" onClick={() => { hwSigRef.current?.clear(); setHwSigEmpty(true); }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> Zurücksetzen
              </button>
            </div>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setHwSigModal(null)} className="w-full sm:w-auto">
              Überspringen
            </Button>
            <Button
              onClick={handleSaveHwSignature}
              disabled={savingHwSig || hwSigEmpty}
              className={`gap-2 w-full sm:w-auto ${hwSigModal?.type === 'ausgabe' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
              data-testid="save-hw-sig-btn"
            >
              {savingHwSig ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Unterschrift speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
